import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import './Dashboard.css';

// Common types
type TimeFilter = '24h' | '7d' | '30d' | 'all';

// Base API response wrapper
interface ApiResponse<T> {
  ok: boolean;
  data: T;
  statusCode: number;
  error?: string;
}

// Common comment structure
interface Comment {
  id: string;
  text: string;
  rating: number;
  comment_date: string;  // ISO date string
}

// Base metric item structure
interface MetricItem {
  tag: string;      // Category name in Spanish
  score: number;    // Score from 1-5
  comments: Comment[];
}

// Response structure for all metric endpoints
interface MetricsResponse {
  metrics: MetricItem[];
  status: 'green' | 'yellow' | 'red';
}

// Status metrics
interface StatusMetrics {
  score: number;    // Overall score as percentage
  message?: string; // Optional message about the rating
  critical: Array<{
    tag: string;    // Category name in Spanish
    score: number;  // Score as percentage
  }>;
}

interface CategoryData {
  data: MetricItem[];
  status: 'green' | 'yellow' | 'red';
}

// Default data constants
const DEFAULT_STATUS_DATA: StatusMetrics = {
  score: 100,
  critical: []
};

const DEFAULT_QUALITY_DATA: CategoryData = {
  data: [
    { tag: "Sabor", score: 5.0, comments: [] },
    { tag: "Presentación", score: 5.0, comments: [] },
    { tag: "Temperatura", score: 5.0, comments: [] }
  ],
  status: "green"
};

const DEFAULT_SERVICE_DATA: CategoryData = {
  data: [
    { tag: "Amabilidad", score: 5.0, comments: [] },
    { tag: "Eficiencia", score: 5.0, comments: [] },
    { tag: "Disponibilidad", score: 5.0, comments: [] },
    { tag: "Conocimiento", score: 5.0, comments: [] }
  ],
  status: "green"
};

const DEFAULT_WAIT_DATA: CategoryData = {
  data: [
    { tag: "Ser sentado", score: 5.0, comments: [] },
    { tag: "Ser atendido", score: 5.0, comments: [] },
    { tag: "Ser servido", score: 5.0, comments: [] }
  ],
  status: "green"
};

const DEFAULT_AMBIENCE_DATA: CategoryData = {
  data: [
    { tag: "Limpieza", score: 5.0, comments: [] },
    { tag: "Comodidad", score: 5.0, comments: [] },
    { tag: "Ruido ambiental", score: 5.0, comments: [] },
    { tag: "Música ambiental", score: 5.0, comments: [] },
    { tag: "Olores", score: 5.0, comments: [] },
    { tag: "Decoración", score: 5.0, comments: [] }
  ],
  status: "green"
};

console.log('Dashboard component is rendering');

const Dashboard = () => {
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('30d');
  const [statusData, setStatusData] = useState<StatusMetrics | null>(null);
  const [qualityData, setQualityData] = useState<CategoryData | null>(null);
  const [serviceData, setServiceData] = useState<CategoryData | null>(null);
  const [waitData, setWaitData] = useState<CategoryData | null>(null);
  const [ambienceData, setAmbienceData] = useState<CategoryData | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricItem | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [timeFilter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [statusRes, qualityRes, serviceRes, waitRes, ambienceRes] = await Promise.all([
        api.get<ApiResponse<StatusMetrics>>(`/api/dashboard/status?time_filter=${timeFilter}`),
        api.get<ApiResponse<MetricsResponse>>(`/api/dashboard/quality?time_filter=${timeFilter}`),
        api.get<ApiResponse<MetricsResponse>>(`/api/dashboard/service?time_filter=${timeFilter}`),
        api.get<ApiResponse<MetricsResponse>>(`/api/dashboard/wait?time_filter=${timeFilter}`),
        api.get<ApiResponse<MetricsResponse>>(`/api/dashboard/ambience?time_filter=${timeFilter}`)
      ]);

      // Validate responses and their data
      if (!statusRes?.ok || !qualityRes?.ok || !serviceRes?.ok || !waitRes?.ok || !ambienceRes?.ok) {
        throw new Error('One or more API requests failed');
      }

      if (!statusRes.data || !qualityRes.data || !serviceRes.data || !waitRes.data || !ambienceRes.data) {
        throw new Error('Missing data in one or more API responses');
      }

      // Process metric responses
      const processMetricResponse = (response: any): CategoryData => ({
        data: response.data.metrics,
        status: response.data.status
      });

      // Update state with processed data
      const status = statusRes.data;
      const quality = processMetricResponse(qualityRes);
      const service = processMetricResponse(serviceRes);
      const wait = processMetricResponse(waitRes);
      const ambience = processMetricResponse(ambienceRes);

      setStatusData((status as unknown) as StatusMetrics);
      setQualityData(quality);
      setServiceData(service);
      setWaitData(wait);
      setAmbienceData(ambience);

      // Log the processed data for debugging
      console.log('Raw API responses:', {
        status: statusRes.data,
        quality: qualityRes.data,
        service: serviceRes.data,
        wait: waitRes.data,
        ambience: ambienceRes.data
      });

      console.log('Processed dashboard data:', {
        status,
        quality,
        service,
        wait,
        ambience
      });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Error al cargar los datos del dashboard');
      // Set default values on error
      setStatusData(DEFAULT_STATUS_DATA);
      setQualityData(DEFAULT_QUALITY_DATA);
      setServiceData(DEFAULT_SERVICE_DATA);
      setWaitData(DEFAULT_WAIT_DATA);
      setAmbienceData(DEFAULT_AMBIENCE_DATA);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTimeFilter(event.target.value as TimeFilter);
  };

  const getStatusColor = (score: number): 'green' | 'yellow' | 'red' => {
    if (score >= 4) return 'green';
    if (score >= 2.5) return 'yellow';
    return 'red';
  };

  const getStatusText = (score: number): string => {
    if (score >= 4) return 'En buen estado';
    if (score >= 2.5) return 'Revisar';
    return 'Revisar urgente';
  };

  const getCategoryStatus = (metrics: MetricItem[]): 'green' | 'yellow' | 'red' => {
    // Find the worst score among all metrics
    const worstScore = Math.min(...metrics.map(metric => metric.score));
    return getStatusColor(worstScore);
  };

  const formatScore = (score: number): string => {
    const roundedScore = Math.round(score * 2) / 2;
    return roundedScore % 1 === 0 ? `${Math.floor(roundedScore)}/5` : `${roundedScore}/5`;
  };

  const renderStars = (score: number) => {
    const totalStars = 5;
    const roundedScore = Math.round(score * 2) / 2; // Round to nearest 0.5
    const fullStars = Math.floor(roundedScore);
    const hasHalfStar = roundedScore % 1 !== 0;
    const emptyStars = totalStars - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="star-rating large">
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} className="material-icons star">star</span>
        ))}
        {hasHalfStar && (
          <span className="material-icons star">star_half</span>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} className="material-icons star">star_outline</span>
        ))}
      </div>
    );
  };

  // Function to format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    // If the date is invalid or in the future, return a fallback.
    if (isNaN(date.getTime()) || diffMs < 0) {
      return 'Fecha inválida'; // Or 'Ahora mismo' / some other fallback
    }

    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const totalDays = Math.floor(diffHours / 24);

    if (totalDays > 0) {
      const totalMonths = Math.floor(totalDays / 30);

      if (totalMonths > 0) {
        const years = Math.floor(totalMonths / 12);
        const monthsInYearComponent = totalMonths % 12;
        const daysInMonthComponent = totalDays % 30; // Days remaining after full months

        let parts: string[] = [];
        if (years > 0) {
          parts.push(`${years} Año${years > 1 ? 's' : ''}`);
        }
        if (monthsInYearComponent > 0) {
          parts.push(`${monthsInYearComponent} Mes${monthsInYearComponent > 1 ? 'es' : ''}`);
        }
        if (daysInMonthComponent > 0) {
          parts.push(`${daysInMonthComponent} Día${daysInMonthComponent > 1 ? 's' : ''}`);
        }
        
        // If after all calculations, parts is empty (e.g. exactly 1 year, 0 months, 0 days based on 30-day months)
        // This check ensures we don't return an empty string if years > 0 but months and days are 0.
        // This case should be covered by parts.push for years.
        // This specific check for parts.length === 0 might be redundant if at least one component (Y, M, D) must be > 0 to be here.
        // However, if totalDays = 360, years=1, monthsInYearComponent=0, daysInMonthComponent=0. parts=["1 Año"]. It's not empty.

        if (parts.length === 0) {
          // This should ideally not be reached if totalMonths > 0,
          // as it implies years or monthsInYearComponent would be > 0.
          // Or if years and months are 0, but daysInMonthComponent > 0 (e.g. 45 days -> 1 month, 15 days).
          // Fallback for safety, or if logic implies totalDays itself should be shown if no Y/M components.
          // Given totalMonths > 0, parts should not be empty.
          // If parts is empty, means totalDays was a multiple of 30 and 360 (e.g. 0), which contradicts totalMonths > 0.
          // Let's default to showing total days if parts somehow ends up empty.
           return `${totalDays} Día${totalDays > 1 ? 's' : ''} atrás`;
        }

        let resultString = parts[0];
        if (parts.length === 2) {
          resultString = `${parts[0]} y ${parts[1]}`;
        } else if (parts.length === 3) {
          resultString = `${parts[0]}, ${parts[1]} y ${parts[2]}`;
        }
        return `${resultString} atrás`;

      } else { // totalMonths is 0, but totalDays > 0 (i.e., 1-29 days)
        return `${totalDays} Día${totalDays > 1 ? 's' : ''} atrás`;
      }
    } else if (diffHours > 0) {
      return `${diffHours} H atrás`;
    } else if (diffMins > 0) {
      return `${diffMins} min atrás`;
    } else {
      return 'Ahora mismo';
    }
  };

  const handleItemClick = (category: string, metric: MetricItem) => {
    setSelectedCategory(category);
    setSelectedMetric(metric);
    setExpandedSection(category);
  };

  const handleCloseDetail = () => {
    setSelectedMetric(null);
    setSelectedCategory(null);
  };

  const handleNavigateMetric = (direction: 'prev' | 'next') => {
    if (!selectedCategory || !selectedMetric) return;

    const categories = [
      { name: 'Calidad', data: qualityData },
      { name: 'Servicio', data: serviceData },
      { name: 'Tiempo de Espera', data: waitData },
      { name: 'Ambiente', data: ambienceData }
    ];

    // Find current category index
    const currentCategoryIndex = categories.findIndex(c => c.name === selectedCategory);
    if (currentCategoryIndex === -1) return;

    const currentCategory = categories[currentCategoryIndex];
    const currentMetricIndex = currentCategory.data?.data.findIndex(m => m.tag === selectedMetric.tag) ?? -1;
    if (currentMetricIndex === -1) return;

    if (direction === 'next') {
      // If not at the last metric in current category
      if (currentMetricIndex < currentCategory.data!.data.length - 1) {
        setSelectedMetric(currentCategory.data!.data[currentMetricIndex + 1]);
      }
      // If at last metric but not last category
      else if (currentCategoryIndex < categories.length - 1) {
        const nextCategory = categories[currentCategoryIndex + 1];
        setSelectedCategory(nextCategory.name);
        setSelectedMetric(nextCategory.data!.data[0]);
        setExpandedSection(nextCategory.name);
      }
    } else {
      // If not at the first metric in current category
      if (currentMetricIndex > 0) {
        setSelectedMetric(currentCategory.data!.data[currentMetricIndex - 1]);
      }
      // If at first metric but not first category
      else if (currentCategoryIndex > 0) {
        const prevCategory = categories[currentCategoryIndex - 1];
        setSelectedCategory(prevCategory.name);
        setSelectedMetric(prevCategory.data!.data[prevCategory.data!.data.length - 1]);
        setExpandedSection(prevCategory.name);
      }
    }
  };

  const getSentimentIcon = (score: number): string => {
    if (score >= 4) return 'sentiment_very_satisfied';
    if (score >= 2.5) return 'sentiment_neutral';
    return 'sentiment_very_dissatisfied';
  };

  const renderCategorySection = (title: string, data: CategoryData | null) => {
    if (!data) return null;
    
    const isExpanded = expandedSection === title;
    const categoryStatus = getCategoryStatus(data.data);
    
    return (
      <div className="expanding-section">
        <h4 
          role="button" 
          onClick={() => setExpandedSection(prev => prev === title ? null : title)} 
          tabIndex={0} 
          onKeyDown={(e) => e.key === 'Enter' && setExpandedSection(prev => prev === title ? null : title)}
        >
          <div className={`circle-arrow ${categoryStatus}`}>
            <span className="material-icons">
              {isExpanded ? 'expand_less' : 'expand_more'}
            </span>
          </div>
          <span className="title">{title}</span>
        </h4>
        {isExpanded && data.data && (
          <div className="items">
            {data.data.map((metric) => (
              <div 
                key={metric.tag} 
                className="item"
                onClick={() => handleItemClick(title, metric)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && handleItemClick(title, metric)}
              >
                <span></span> {/* Empty first column */}
                <span className="item-label">{metric.tag}</span>
                <div className="item-content">
                  <span className={`status-bubble ${getStatusColor(metric.score)}`}>
                    {getStatusText(metric.score)}
                  </span>
                  <button 
                    className="arrow-button"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click when clicking the button
                      handleItemClick(title, metric);
                    }}
                    title="Ver detalles"
                  >
                    <span className="material-icons">chevron_right</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando datos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={fetchDashboardData} className="retry-button">
          Reintentar
        </button>
      </div>
    );
  }

  if (!statusData || !qualityData || !serviceData || !waitData || !ambienceData) {
    return (
      <div className="error-container">
        <p className="error-message">No se pudieron cargar los datos</p>
        <button onClick={fetchDashboardData} className="retry-button">
          Reintentar
        </button>
      </div>
    );
  }

  const sortedCritical = [...statusData.critical].sort((a, b) => a.score - b.score);

  return (
    <div className="dashboard">
      <div className="dashboard-main">
        <div className="dashboard-header">
          <h2>Panel de Control</h2>
          <select 
            className="time-select"
            value={timeFilter}
            onChange={handleTimeFilterChange}
          >
            <option value="24h">Últimas 24 horas</option>
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="all">Todo el tiempo</option>
          </select>
        </div>

        <div className="top-widgets">
          {statusData && (
            <>
              <div className="widget detalle">
                <h3 className="widget-title">Categorías a Mejorar</h3>
                <div className="widget-box">
                  {sortedCritical.map((category) => {
                    // Convert the percentage score to a 1-5 scale for color determination
                    const scoreOn5Scale = category.score / 20;
                    
                    return (
                      <div key={category.tag} className="category-bar">
                        <span className="category-name">{category.tag}</span>
                        <div className="bar-container">
                          <div 
                            className={`bar ${getStatusColor(scoreOn5Scale)}`}
                            style={{ width: `${Math.max(20, Math.min(100, category.score))}%` }}
                          >
                            {category.score.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="widget promedio-satisfaccion">
                <h3 className="widget-title">Rating General</h3>
                <div className="widget-box">
                  <div className="satisfaction-score">
                    <div className="rating-row">
                      {renderStars(statusData.score / 20)}
                      <div className="score-value">{formatScore(statusData.score / 20)}</div>
                    </div>
                    <div className="satisfaction-message">
                      {statusData.message || "El restaurante mantiene un buen nivel de servicio en general, pero hay áreas que requieren atención."}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="bottom-widgets">
          <div className="widget satisfaccion-cliente">
            <h3 className="widget-title">Satisfacción del Cliente</h3>
            <div className="widget-box">
              <div className="satisfaction-table">
                <div className="items-header">
                  <span>Categoría a Evaluar</span>
                  <span>Criterio</span>
                  <span>Estado</span>
                </div>
                {renderCategorySection('Calidad', qualityData)}
                {renderCategorySection('Servicio', serviceData)}
                {renderCategorySection('Tiempo de Espera', waitData)}
                {renderCategorySection('Ambiente', ambienceData)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {selectedMetric && (
        <div className="detail-panel">
          <div className="detail-header">
            <h3 className="detail-title">Detalle</h3>
            <button className="close-button" onClick={handleCloseDetail}>
              <span className="material-icons">close</span>
            </button>
          </div>

          <div className="navigation-controls">
            <button 
              className="nav-button"
              onClick={() => handleNavigateMetric('prev')}
              disabled={!selectedCategory || (selectedCategory === 'Calidad' && selectedMetric?.tag === qualityData?.data[0].tag)}
            >
              <span className="material-icons">chevron_left</span>
            </button>
            <span className="metric-name">{selectedMetric.tag}</span>
            <button 
              className="nav-button"
              onClick={() => handleNavigateMetric('next')}
              disabled={!selectedCategory || (selectedCategory === 'Ambiente' && selectedMetric?.tag === ambienceData?.data[ambienceData?.data.length - 1].tag)}
            >
              <span className="material-icons">chevron_right</span>
            </button>
          </div>

          <div className="detail-metrics">
            <div className="metric-bubble">
              <span className="metric-label">Rating</span>
              <div className="metric-value">
                <span className="material-icons">star</span>
                {selectedMetric.score.toFixed(1)}/5
              </div>
            </div>
            <div className="metric-bubble">
              <span className="metric-label">Sentimiento</span>
              <div className="metric-value">
                <span className="material-icons">
                  {getSentimentIcon(selectedMetric.score)}
                </span>
              </div>
            </div>
          </div>

          <div className="comments-section">
            <h4 className="comments-title">Comentarios</h4>
            <div className="detail-comments">
              {selectedMetric.comments.map((comment) => (
                <div key={comment.id} className="detail-comment">
                  <p className="comment-text">{comment.text}</p>
                  <span className="comment-time">
                    {formatRelativeTime(comment.comment_date)}
                  </span>
                </div>
              ))}
              {selectedMetric.comments.length === 0 && (
                <p className="no-comments">No hay comentarios disponibles</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
